import React, { useRef } from 'react';
import { useUIStore } from '../../../stores/useUIStore';
import { useAuthStore } from '../../../stores/useAuthStore';
import { ModalRoot } from '../Modal/ModalRoot';
import { isTvClient } from '../../../utils/tvClient';
import TvVideoPlayer from './TvVideoPlayer';
import '../../ui/BrandFrame/BrandFrame.css';
import './MediaModal.css';

const MediaModal: React.FC = () => {
  const { openMediaUrl, mediaType, isMediaModalOpen, closeMediaModal } = useUIStore();
  const revalidateSessionAfterMediaError = useAuthStore((state) => state.revalidateSessionAfterMediaError);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  if (!isMediaModalOpen || !openMediaUrl || !mediaType) {
    return null;
  }

  // On the TV, Back closes the modal (useTvBackBridge), so there is no close button, and
  // videos use the remote-driven player instead of unreachable native controls.
  const isTv = isTvClient();
  const isVideo = mediaType === 'video';
  const overlayClassName = isTv ? 'video-modal-overlay video-modal-overlay--tv' : 'video-modal-overlay';
  const modalLabel = isVideo ? 'Video Player' : 'Image Viewer';
  const closeLabel = isVideo ? 'Close video' : 'Close image';

  return (
    <ModalRoot
      onClose={closeMediaModal}
      ariaLabel={modalLabel}
      initialFocusRef={isVideo ? videoRef : imageRef}
      overlayClassName={overlayClassName}
      dialogClassName="video-modal-content"
      data-testid="media-modal"
    >
        {!isTv && (
          <button
            className="video-modal-close-button"
            onClick={closeMediaModal}
            aria-label={closeLabel}
            type="button"
          >
            &times;
          </button>
        )}

        <div className="video-modal-container">
          <div className="video-modal-gradient-border">
            <div className="video-modal-video-wrapper">
              {isVideo && isTv && (
                <TvVideoPlayer ref={videoRef} src={openMediaUrl} onError={revalidateSessionAfterMediaError} />
              )}
              {isVideo && !isTv && (
                <video
                  ref={videoRef}
                  src={openMediaUrl}
                  controls
                  autoPlay
                  playsInline
                  className="video-modal-video"
                  aria-label="Video content"
                  onError={revalidateSessionAfterMediaError}
                >
                  Your browser does not support the video tag.
                </video>
              )}
              {!isVideo && (
                <img
                  ref={imageRef}
                  src={openMediaUrl}
                  alt="Full size view"
                  className="video-modal-video video-modal-image"
                  tabIndex={0}
                  aria-label="Image content"
                  onError={revalidateSessionAfterMediaError}
                />
              )}
            </div>
          </div>
        </div>
    </ModalRoot>
  );
};

export default MediaModal;
